import React, { useState, useRef, useEffect } from "react";
import { FiGlobe, FiChevronDown, FiCheck } from "react-icons/fi";
import { useLanguage, SUPPORTED_LANGUAGES } from "../../context/LanguageContext";

export default function LanguageSelector({ compact = false, className = "" }) {
  const { currentLanguage, changeLanguage, loading } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [isChanging, setIsChanging] = useState(false);
  const dropdownRef = useRef(null);

  const currentLang = SUPPORTED_LANGUAGES.find(l => l.code === currentLanguage) || SUPPORTED_LANGUAGES[0];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLanguageChange = async (langCode) => {
    if (langCode === currentLanguage) {
      setIsOpen(false);
      return;
    }

    setIsChanging(true);
    try {
      await changeLanguage(langCode);
    } catch (error) {
      console.error("Failed to change language:", error);
    } finally {
      setIsChanging(false);
      setIsOpen(false);
    }
  };

  if (loading) return null;

  return (
    <div ref={dropdownRef} className={`relative ${className}`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={isChanging}
        className={`flex items-center gap-2 px-3 py-2 rounded-xl border border-slate-200
          bg-white hover:bg-slate-50 transition-all duration-200 ${compact ? "text-sm" : ""}
          ${isChanging ? "opacity-50 cursor-wait" : ""}`}
      >
        <FiGlobe className="w-4 h-4 text-slate-500" />
        <span className="font-medium text-slate-700">
          {compact ? currentLang.code.toUpperCase() : currentLang.nativeName}
        </span>
        <FiChevronDown
          className={`w-4 h-4 text-slate-400 transition-transform duration-200
            ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-52 bg-white rounded-xl shadow-xl border border-slate-200 py-2 z-50 animate-in fade-in slide-in-from-top-2 duration-200">
          <div className="px-3 py-2 border-b border-slate-100">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
              Select Language
            </p>
          </div>
          <div className="py-1">
            {SUPPORTED_LANGUAGES.map((lang) => (
              <button
                key={lang.code}
                onClick={() => handleLanguageChange(lang.code)}
                disabled={isChanging}
                className={`w-full flex items-center justify-between px-4 py-2.5
                  hover:bg-slate-50 transition-colors duration-150 ${
                    currentLanguage === lang.code
                      ? "bg-emerald-50 text-emerald-700"
                      : "text-slate-700"
                  } ${isChanging ? "opacity-50 cursor-wait" : ""}`}
              >
                <div className="flex items-center gap-3">
                  <span className="text-lg">{lang.flag}</span>
                  <div className="flex flex-col items-start">
                    <span className="font-medium">{lang.nativeName}</span>
                    <span className="text-xs text-slate-500">{lang.name}</span>
                  </div>
                </div>
                {currentLanguage === lang.code && (
                  <FiCheck className="w-5 h-5 text-emerald-600" />
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
